import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import SsoHandler from "../utils/SSOhandler";
import localforage from "localforage";
import { useUserInfo } from "./user";
import apiClient, { request, ReqMethods } from "../lib/apiClient";
import { uploadFileInChunks } from "../lib/chunkedUpload";
import { useWhitelabelAgency } from "../queries/agency";
import { agentsKeys, fetchAgentsByUser } from "../queries/agents";
import { locationUsersKeys, useLocationUsers } from "../queries/settingsAccount";
import useDebouncedEffect from "../hook/debounce";
import { useNavigate, useParams } from "react-router-dom";
import ContactToSupportModal from "../components/ContactToSupportModal";
import toast from "react-hot-toast";
import moment from "moment-timezone";

const UsersContext = createContext();

const useUsersContext = () => useContext(UsersContext);

const { GET } = ReqMethods;

const UsersProvider = ({ children }) => {
  const { SSO, checkSSO } = SsoHandler();
  const userActiveLocation = useUserInfo()[4];
  const userInfo = useUserInfo()[0];
  const [openShareModal, setOpenShareModal] = useState(null);

  const [isActiveSide, setIsActiveSide] = useState(true);
  const [users, setUsers] = useState("");
  const [userTyping, setUserTyping] = useState({});
  const [isUserChating, setIsUserChating] = useState(false);
  const [agentPlaceholderText, setAgentPlaceholderText] = useState("");
  const [fromPromptMsg, setFromPromptMsg] = useState({});
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentPageLoading, setAgentPageLoading] = useState(false);
  const [agentBuilder, setAgentBuilder] = useState(null);
  const [agency, setAgency] = useState(null);
  const [openAgentBuilder, setOpenAgentBuilder] = useState(false);
  const [useAgent, setUseAgent] = useState(false);
  const [isAuto, setIsAuto] = useState(true);
  const [conversationId, setConversationId] = useState("");
  const [conversations, setConversations] = useState({
    totalChats: 0,
    hasMore: false,
    data: [],
  });
  const [chatError, setChatError] = useState({
    showError: false,
    errorMessage: "",
    iconColor: "",
    classNames: "",
  });
  const [searchPageContent, setSearchPageContent] = useState({
    data: [],
    page: 1,
    limit: 30,
    total: 0,
  });
  const [tasksData, setTasksData] = useState([]);
  const navigate = useNavigate();
  const [openLimitModal, setOpenLimitModal] = useState(false);
  const params = useParams();
  const [messages, setMessages] = useState([]);
  const queryClient = useQueryClient();

  const {
    whitelabel,
    isLoading: isAgencyLoading,
    isSuccess: isAgencySuccess,
    error: agencyError,
  } = useWhitelabelAgency();
  useEffect(() => {
    if (whitelabel) {
      setAgency(whitelabel);
      changeFavicon(whitelabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whitelabel]);

  const changeFavicon = (agency) => {
    // Replace 'new-favicon.ico' with the path to your new favicon
    const newFavicon = agency?.Customization?.chatapp_logo || "";

    // Create a new link element
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = newFavicon;

    // Find the existing favicon link element in the head
    const head = document.head || document.getElementsByTagName("head")[0];
    const existingFavicon = document.querySelector("link[rel*='icon']");

    // If an existing favicon is found, remove it
    if (existingFavicon) {
      head.removeChild(existingFavicon);
    }

    // Append the new favicon link element to the head
    head.appendChild(link);
  };

  const [showExploreContent, setShowExploreContent] = useState(false);
  const [companyInfo, setCompanyInfo] = useState();
  const checkfreetrial = async (activeLocation) => {
    if (activeLocation?.id && activeLocation.type === "free_trail") {
      const response = await apiClient.get(
        `/auth/checktrail?location_id=${activeLocation?.id}`,
      );
      if (response) {
        return response;
      }
    }
  };
  // Deferred until a consumer actually needs the list (Team settings, a
  // share modal, Nango, …) via requestLocationUsers(), instead of fetching
  // on every app load regardless of whether anything uses it.
  const [locationUsersRequested, setLocationUsersRequested] = useState(false);
  const requestLocationUsers = () => setLocationUsersRequested(true);
  const { data: locationUsers = [], isLoading: isLocationUsersLoading } = useLocationUsers(
    userActiveLocation?.id,
    { enabled: locationUsersRequested },
  );

  // Thin wrapper kept so existing callers (AddUserModal/EditUserModal/
  // DeleteUserModal on mutation success) don't need to change — it now
  // invalidates the useLocationUsers query above instead of imperatively
  // refetching and setting local state.
  const getLocationUsers = (locationId) => {
    queryClient.invalidateQueries({
      queryKey: locationUsersKeys.list(locationId || userActiveLocation?.id),
    });
  };

  const getOrCreateAgentBuilder = async () => {
    if (userInfo.role === "admin") {
      const response = await apiClient.post(`/assistant/agent-builder`, {
        profile_id: userActiveLocation?.id,
      });
      if (response?.data) setAgentBuilder(response?.data?.data?.assistant_id);
    }
  };

  const getSaasProducts = async () => {
    try {
      // exclude_plugins: consumers read plan pricing fields and
      // agenct_config.agent_limit, never the ~590KB-per-plan plugin catalogue.
      const response = await request(GET, "/saas/agencysaas", {
        query: `agency_id=${userActiveLocation?.agency_id}&exclude_plugins=true`,
      });
      if (response?.data) {
        let arraySaas = response?.data?.map((product) => ({
          ...product,
          activePlan: product._id === userActiveLocation?.plan_id,
          buttonLabel:
            product._id === userActiveLocation?.plan_id
              ? "Current plan"
              : "Upgrade plan",
        }));

        const hasActivePlan = arraySaas.find((product) => product.activePlan);
        if (!hasActivePlan) {
          arraySaas = arraySaas.map((product) => ({
            ...product,
            activePlan: product.is_free_saas === true,
            buttonLabel:
              product.is_free_saas === true ? "Current plan" : "Upgrade plan",
          }));
        } else {
          arraySaas = response?.data?.map((product) => ({
            ...product,
            activePlan: product._id === userActiveLocation?.plan_id,
            buttonLabel: product?.is_free_saas
              ? "Change plan"
              : product._id === userActiveLocation?.plan_id
                ? "Current plan"
                : hasActivePlan &&
                  (parseInt(hasActivePlan.monthly_amount) >
                    parseInt(product.monthly_amount) ||
                    parseInt(hasActivePlan.yearly_amount) >
                    parseInt(product.yearly_amount))
                  ? "Change plan"
                  : "Upgrade plan",
          }));
        }
        setProducts(arraySaas);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const isHighestPlan = () => {
    if (products.length === 0) return false;
    const sortedProducts = [...products]
      .filter((item) => item.isenable_account)
      .sort((a, b) => {
        const aPrice =
          userActiveLocation?.planType === "monthly"
            ? a.monthly_amount || 0
            : a.yearly_amount || 0;

        const bPrice =
          userActiveLocation?.planType === "monthly"
            ? b.monthly_amount || 0
            : b.yearly_amount || 0;
        return bPrice - aPrice;
      });
    return sortedProducts[0]._id === userActiveLocation?.plan_id;
  };

  useEffect(() => {
    userActiveLocation && getSaasProducts();
  }, [userActiveLocation]);

  // Function to toggle showExploreContent
  const enableExplore = () => {
    setShowExploreContent(true);
  };

  const enableChat = () => {
    setShowExploreContent(false);
  };

  // Was a raw axios.get(`${BASE_URL}/assistant/fetchbyuser...`) — now routed
  // through the shared useAgentsList query cache (see src/queries/agents.js),
  // also used by ApiAccess/index.js, integration/index.js, addwebsites/index.js
  // and uploadFiles/index.js. staleTime: 0 forces a real refetch every call
  // (matching the old "always hit the network" axios behavior) and — because
  // it writes into the SAME cache entry those other call sites read from via
  // useAgentsList — also pushes the fresh list out to them, which is what
  // makes handleRefresh() after a create/update/delete actually propagate now.
  const fetchAgentsArray = async () => {
    setAgentPageLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: agentsKeys.list(userInfo?._id, userActiveLocation?.id),
        queryFn: () => fetchAgentsByUser(userInfo?._id, userActiveLocation?.id),
        staleTime: 0,
      });
      const updatedResponse = (data?.data || []).filter((item) => item.ui_enable);
      setAgents(updatedResponse);
      setAgentPageLoading(false);
      return updatedResponse;
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
    setAgentPageLoading(false);
  };

  useDebouncedEffect(
    () => {
      if (userActiveLocation?.id) {
        getOrCreateAgentBuilder();
      }
    },
    [userActiveLocation],
    300,
  );

  const [toastState, setToast] = useState({ message: "", isVisible: false });


  const totalAttachedBytesRef = useRef(0);
  const resetAttachedFileSize = () => {
    totalAttachedBytesRef.current = 0;
  };
  const getAttachedFileSize = () => totalAttachedBytesRef.current;

  const showToast = (message) => {
    setToast({ message, isVisible: true });
    setTimeout(() => setToast({ message: "", isVisible: false }), 3000); // Auto-hide after 3 seconds
  };

  const hideToast = () => {
    setToast({ message: "", isVisible: false });
  };

  const hideLeftSidebar = () => {
    setIsActiveSide(false);
  };

  const showLeftSidebar = () => {
    setIsActiveSide(true);
  };

  const _updateUserProp = (userId, prop, value) => {
    setUsers((users) => {
      const usersCopy = [...users];
      let userIndex = users.findIndex((user) => user.id === userId);
      const userObject = usersCopy[userIndex];
      usersCopy[userIndex] = { ...userObject, [prop]: value };
      setUserTyping(usersCopy);
      return usersCopy;
    });
  };

  const checkUserAuth = async () => {
    return await localforage.getItem("user_data");
  };

  const getCompanyInfo = async () => {
    const agency = await localforage.getItem("agency");
    if (agency?.agency_ghl) {
      try {
        const response = await apiClient.get(
          `/agency/companyinfo?company_id=${agency?.agency_ghl?.company_id}`,
        );
        if (response) {
          setCompanyInfo(response?.data?.data);
        }
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
    }
  };
  const sessionstore = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const sessionKey = url.searchParams.get("sessionKey");
    if (sessionKey) {
      localforage.setItem("sessionKey", sessionKey);
    }
  };
  // These used to run together in one effect keyed on [SSO, location id];
  // both dependencies settle during boot, so companyinfo and chatusers each
  // fired 2-3x per page load. companyinfo and the sessionKey capture are
  // boot-once; chatusers follows the active location.
  useEffect(() => {
    getCompanyInfo();
    sessionstore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    checkSSO();
  }, []);

  const setUserAsUnread = (userId) => {
    _updateUserProp(userId, "unread", 0);
  };

  // Uploads each chat attachment in small chunks (lib/chunkedUpload) instead
  // of one multipart POST, so a large attachment doesn't trip Cloud Run's
  // request-size limit (413). Audio and non-audio files used to go to the
  // same /chat/multimedia endpoint via two separate FormData payloads (the
  // "translate" field multimedia never actually reads) — now they go through
  // the same chunked compType for the same reason: there's no behavioral
  // difference server-side, so there's no reason to keep them split.
  const contextuploadFilefrominput = async (
    files,
    chatId = "",
    setSpinnerLoading,
  ) => {
    const MAX_COMBINED_FILE_SIZE = 50 * 1024 * 1024;
    const getRawFile = (item) => (item?.file ? item.file : item);
    const getFileSize = (f) =>
      Number(f?.size ?? f?.sizeBytes ?? f?.fileSize ?? f?.bytes ?? 0) || 0;

    let combinedSize = 0;
    for (let i = 0; i < files.length; i++) {
      const rawFile = getRawFile(files[i]);
      const size = getFileSize(rawFile);
      combinedSize += size;
    }

    if (totalAttachedBytesRef.current + combinedSize > MAX_COMBINED_FILE_SIZE) {
      const msg = "Your selected files exceed the 50 MB upload limit. Remove some files or upload them in smaller batches.";
      toast.error(msg);
      if (setSpinnerLoading) setSpinnerLoading(false);
      return { status: false, message: msg };
    }
    totalAttachedBytesRef.current += combinedSize;

    let fileError = false;
    let errorMessage = "";
    const uploadedFiles = [];

    for (const entry of files) {
      const rawFile = getRawFile(entry);
      try {
        const response = await uploadFileInChunks(rawFile, {
          profileId: userActiveLocation.id,
          username: userInfo?.userName || userActiveLocation?.business_name,
          compType: "chat_attachment",
        });
        if (response?.success) {
          // ChatInput.jsx's selectedFiles list reads a plain `.name` off each
          // entry (it's what the original /chat/multimedia response shaped
          // each attachment as: {name: file_id}) — not the richer per-result
          // object the chunked backend returns.
          for (const result of response.data?.results || []) {
            if (result.success && result.file_id) uploadedFiles.push({ name: result.file_id });
          }
        } else {
          fileError = true;
          errorMessage = response?.message;
        }
      } catch (error) {
        console.log(error);
        fileError = true;
        errorMessage = error?.response?.data?.message || error.message;
      }
    }

    setSpinnerLoading(false);

    if (fileError || uploadedFiles.length === 0) {
      // The combined-size counter was reserved optimistically before this
      // attempt's network call; a failed upload must give those bytes back
      // or a retry gets wrongly blocked by the combined-size check next time.
      totalAttachedBytesRef.current = Math.max(
        0,
        totalAttachedBytesRef.current - combinedSize,
      );
      return {
        status: fileError,
        transcript: "",
        message: errorMessage,
      };
    }

    return {
      data: {
        success: true,
        filetype: "othertype",
        data: uploadedFiles,
      },
    };
  };

  const contextUploadFileOnboarding = async (
    files,
    chatId = "",
    setSpinnerLoading,
  ) => {
    // return 0
    const audioformData = new FormData();
    const formData = new FormData();
    const audiofilestype = [
      "audio/mpeg", // mp3
      "video/mp4", // mp4
      "video/mpeg", // mpeg
      "audio/mp4", // m4a
      "audio/wav", // wav
    ];
    const audiofiles = [];
    const otherFiles = [];
    let audioTranscript = "";
    let fileError = false;
    let errorMessage = "";

    for (let index = 0; index < files.length; index++) {
      const fileType = files[index].file?.type;

      if (audiofilestype.includes(fileType)) {
        audiofiles.push(files[index]);
      } else {
        otherFiles.push(files[index]);
      }
    }

    // Append allowed files to formData

    for (let file of audiofiles) {
      audioformData.append("file", file.file ? file.file : file);
      audioformData.append("translate", file.isChecked);
    }
    for (let file of otherFiles) {
      formData.append("file", file.file ? file.file : file);
    }
    audioformData.append("profile_id", userActiveLocation.id);
    audioformData.append("purpose", "context_wrapper");
    audioformData.append("username", userActiveLocation?.business_name);
    audioformData.append("model_name", "openai/gpt-4o");
    formData.append("profile_id", userActiveLocation.id);
    formData.append("username", userActiveLocation?.business_name);
    formData.append("model_name", "openai/gpt-4o");

    if (audiofiles.length > 0) {
      try {
        const response = await apiClient.post(
          `/chat/multimediaForOnboarding`,
          audioformData,
          {
            "Content-Type": "multipart/form-data",
          },
        );
        if (response) {
          return response;
        }
        audioTranscript = response;
      } catch (error) {
        console.log(error);
        fileError = true;
      }
    }

    if (otherFiles.length > 0) {
      try {
        const response = await apiClient.post(
          `/chat/multimediaForOnboarding`,
          formData,
          {
            "Content-Type": "multipart/form-data", // Optional: axios sets this automatically
          },
        );
        if (response) {
          setSpinnerLoading(false);
          return response;
        }
      } catch (error) {
        setSpinnerLoading(false);
        fileError = true;
        errorMessage = error?.response?.data?.message;
      }
    }
    if (fileError) {
      setSpinnerLoading(false);
      return {
        status: fileError,
        transcript: audioTranscript,
        message: errorMessage,
      };
    } else {
      setSpinnerLoading(false);
      return {
        status: "uploaded",
        transcript: audioTranscript,
      };
    }
  };

  // `locationId` override lets a caller that just switched workspace pass the
  // new id explicitly — this closure's own `userActiveLocation` is still the
  // pre-switch value until UsersProvider re-renders, so the default would
  // fetch the old account's chats.
  const fetchChats = async (page = 0, locationId = userActiveLocation?.id) => {
    try {
      const skip = page === 0 ? 0 : (page - 1) * 50 + 50;
      const response = await apiClient.get(
        `/chat/filter?locationId=${locationId}&userId=${userInfo?._id}&limit=${50}&skip=${skip}`,
      );
      const chats = response?.data?.data;
      const totalChats = response?.data?.totalChats;
      if (page === 0)
        setConversations({
          totalChats,
          data: chats,
          hasMore:
            page + 1 <
            Math.ceil(
              totalChats / (page === 0 ? 0 * 100 + 50 : (page - 1) * 100 + 50),
            ),
        });
      else
        setConversations((prev) => ({
          ...prev,
          totalChats,
          data: {
            favChats: [...prev?.data?.favChats, ...chats.favChats],
            today: [...prev?.data?.today, ...chats.today],
            yesterday: [...prev?.data?.yesterday, ...chats.yesterday],
            last7Day: [...prev?.data?.last7Day, ...chats.last7Day],
          },
          hasMore:
            page + 1 <
            Math.ceil(
              totalChats / (page === 0 ? 0 * 100 + 50 : (page - 1) * 100 + 50),
            ),
        }));
    } catch (err) {
      toast.error(err.response.data.message || err.message || err.toString());
    }
  };

  const skipNextFetchIdRef = useRef(null);
  // Holds the just-sent optimistic message until the next real fetch confirms
  // it (or doesn't, in which case we keep showing it instead of letting the
  // server list silently drop it). Cleared after that one fetch either way.
  const pendingMessageRef = useRef(null);
  const markMessagePending = (msg) => {
    pendingMessageRef.current = msg;
  };

  const fetchMessages = async (id) => {
    if (skipNextFetchIdRef.current === id) {
      skipNextFetchIdRef.current = null;
      return messages;
    }
    try {
      const response = await apiClient.get(
        `/chat/messages?id=${id}&locationId=${userActiveLocation?.id}&userId=${userInfo?._id}`,
      );
      const fetched = response?.data?.data || [];
      const pending = pendingMessageRef.current;
      pendingMessageRef.current = null;
      const pendingConfirmed =
        !pending ||
        fetched.some((m) => !m.sender && m.content === pending.content);
      const data = pendingConfirmed ? fetched : [...fetched, pending];
      setMessages(data);
      return data;
    } catch (err) {
      toast.error(err.response.data.message || err.message || err.toString());
    }
  };
  const PENDING_CHAT_TTL = 30 * 60 * 1000; // 30 minutes
  const pendingPollRef = useRef(null);

  const pendingChatKey = (id) => `pendingChat:${id}`;

  const markChatPending = (id) => {
    if (!id) return;
    try {
      localStorage.setItem(pendingChatKey(id), String(Date.now()));
    } catch (e) { }
  };

  const clearChatPending = (id) => {
    if (!id) return;
    try {
      localStorage.removeItem(pendingChatKey(id));
    } catch (e) { }
  };

  const stopPendingPoll = () => {
    if (pendingPollRef.current) {
      clearInterval(pendingPollRef.current);
      pendingPollRef.current = null;
    }
  };

  const startPendingPoll = (id, startedAt) => {
    stopPendingPoll();
    pendingPollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > PENDING_CHAT_TTL) {
        stopPendingPoll();
        clearChatPending(id);
        setIsUserChating(false);
        setAgentPlaceholderText(null);
        return;
      }
      const data = await fetchMessages(id);
      const list = Array.isArray(data) ? data : [];
      const last = list[list.length - 1];
      if (last && last.sender) {
        stopPendingPoll();
        clearChatPending(id);
        setIsUserChating(false);
        setAgentPlaceholderText(null);
      }
    }, 10000);
  };

  const resumePendingChat = (id, loadedMessages) => {
    if (!id) return;
    stopPendingPoll();
    let startedAt;
    try {
      startedAt = Number(localStorage.getItem(pendingChatKey(id)));
    } catch (e) {
      return;
    }
    if (!startedAt) return;
    if (Date.now() - startedAt > PENDING_CHAT_TTL) {
      clearChatPending(id);
      return;
    }
    const list = Array.isArray(loadedMessages) ? loadedMessages : [];
    const last = list[list.length - 1];
    // Reply already landed while the tab was away — nothing to wait for.
    if (last && last.sender) {
      clearChatPending(id);
      return;
    }
    setIsUserChating(true);
    setAgentPlaceholderText("Agent is thinking...");
    startPendingPoll(id, startedAt);
  };

  useEffect(() => {
    return () => stopPendingPoll();
  }, []);

  const isCreatingRef = useRef(false);

  const createConvesation = async (payload = {}) => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setIsUserChating(true);
    const hasAssistantOverride =
      Boolean(payload?.assistant_id) ||
      Boolean(payload?.assistant_name) ||
      Boolean(payload?.assistant_model_name);
    const activeAssistant = hasAssistantOverride
      ? {
        ...(payload?.assistant_model_name && {
          modal_name: payload.assistant_model_name,
        }),
        ...(payload?.assistant_id && { assistant_id: payload.assistant_id }),
        ...(payload?.assistant_name && {
          assistant_name: payload.assistant_name,
        }),
      }
      : selectedAssistant;
    const outgoingMessage = activeAssistant?.message || payload.message;
    const chatName = payload.title || outgoingMessage;

    try {
      const chatPayload = {
        locationId: userActiveLocation?.id,
        username: userInfo?.userName,
        userId: {
          id: userInfo?._id,
          type: "author",
          username: userInfo?.userName,
        },
        ...(activeAssistant?.assistant_name && {
          assistant_name: activeAssistant.assistant_name,
        }),
        ...(activeAssistant?.assistant_id && {
          assistant_id: activeAssistant.assistant_id,
        }),
        timezone: moment.tz.guess() || "UTC",
      };

      let conversationId = params.id;

      if (activeAssistant?.message !== "edit mode") {
        const response = await apiClient.post(`/chat`, chatPayload);

        const data = response.data.data;
        conversationId = data.conversation_id;

        const chat = {
          conversation_id: data.conversation_id,
          userId: data.userId,
          name: chatName,
          createdAt: data.createdAt,
        };

        if (
          activeAssistant?.assistant_name !== "Agent Builder" ||
          !activeAssistant?.assistant_name
        ) {
          setConversations((prev) => ({
            ...prev,
            data: {
              ...prev?.data,
              today: [chat, ...(prev?.data?.today ? prev?.data?.today : [])],
            },
          }));
        }

        if (payload?.addInMessages) skipNextFetchIdRef.current = conversationId;
        navigate(`/app/chat/${conversationId}`);
      }

      const newMsgObject = {
        ...(payload.title ? { title: payload.title } : {}),
        content: outgoingMessage,
        sender: null,
        time: new Date().toLocaleTimeString(),
        status: "delivered",
        file_type: payload?.messagetype || "KB",
        assistant_name: activeAssistant?.assistant_name || "",
        file_ids:
          activeAssistant?.message === "edit mode" ||
            activeAssistant?.message === "force restart"
            ? []
            : payload?.fileData,
      };
      const messagePayload = {
        conversation_id: conversationId || activeAssistant?.conversationId,
        type: "savetodb",
        message: newMsgObject,
        assistant: activeAssistant,
      };

      if (payload?.addInMessages) {
        const optimisticMessage = payload.title
          ? { ...newMsgObject, content: newMsgObject.title }
          : newMsgObject;
        setMessages([optimisticMessage]);
        markMessagePending(optimisticMessage);
      }

      await handleSendMessage(messagePayload);
    } catch (err) {
      setIsUserChating(false);
      toast.error(err.response.data.message || err.message || err.toString());
    } finally {
      isCreatingRef.current = false;
      setSelectedAssistant({
        ...selectedAssistant,
        assistant_id: null,
        message: null,
        conversationId: null,
      });
    }
  };

  const handleSendMessage = async (payload) => {
    if (!isUserChating) setIsUserChating(true);
    stopPendingPoll();
    markChatPending(payload?.conversation_id);
    if (chatError.showError)
      setChatError({
        showError: false,
        errorMessage: "",
        classNames: "",
        iconColor: "",
      });
    setAgentPlaceholderText("Selecting an Agent...");
    const agentTimeout = setTimeout(() => {
      setAgentPlaceholderText("Agent Selected");
    }, 2000);
    const activeAssistant = payload?.assistant || selectedAssistant;
    const assistant = {
      modal_name:
        activeAssistant?.assistant_name === "Agent Builder"
          ? "openai/gpt-4o.1"
          : activeAssistant?.modal_name || "openai/gpt-4o",
      ...(activeAssistant?.assistant_id
        ? { assistant_id: activeAssistant?.assistant_id }
        : {}),
      ...(activeAssistant?.assistant_name
        ? { assistant_name: activeAssistant?.assistant_name }
        : {}),
    };
    try {
      const combinedPayload = {
        conversation_id: payload.conversation_id,
        type: payload.type,
        message: payload.message,
        username: userInfo?.userName,
        user_id: userInfo?.userId,
        agency_id: userActiveLocation?.agency_id,
        timezone: moment.tz.guess() || "UTC",
        assistant_model_name: assistant?.modal_name,
        ...(assistant?.assistant_id
          ? { assistant_id: assistant.assistant_id }
          : {}),
        ...(assistant?.assistant_name
          ? { assistant_name: assistant.assistant_name }
          : {}),
      };
      await apiClient.post(
        `/chat/handleChatMessage`,
        combinedPayload,
        {
          timeout: 1800000,
        }
      );
      clearTimeout(agentTimeout);
      await fetchMessages(payload.conversation_id);
      setIsUserChating(false);
      setAgentPlaceholderText(null);
    } catch (error) {
      clearTimeout(agentTimeout);
      if (
        userActiveLocation?.type !== "free" &&
        error.response.data.errorType !== "balance"
      ) {
        setIsUserChating(false);
        setAgentPlaceholderText(null);
        navigate("/app/settings", {
          state: {
            tab: "Pricing",
            text: "Upgrade to add more AI Agent tasks",
          },
        });
      }
      const activePlan = products?.find((item) => item?.activePlan === true);
      if (
        (userActiveLocation?.type === "free" ||
          (!activePlan?.isenable_account && !activePlan?.developer_plan) ||
          isHighestPlan()) &&
        error.response.data.message === "Reached to limit"
      ) {
        setIsUserChating(false);
        setAgentPlaceholderText(null);
        setOpenLimitModal(true);
      }
      if (error.response.data.errorType === "balance") {
        setIsUserChating(false);
        setAgentPlaceholderText(null);
        setToast({ messages: error.response.data.message, isVisible: true });
        setTimeout(() => setToast({ message: "", isVisible: false }));
      }

      const statusCode = error?.response?.status;
      const backendErrorMessage = String(
        error?.response?.data?.error?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while getting the agent response.",
      );

      setChatError({
        showError: true,
        errorMessage:
          statusCode === 500
            ? "This is not working right now. Please try again in a few minutes or contact us using the chat bubble bottom right."
            : backendErrorMessage.includes(
              "No assistant could handle this request",
            )
              ? "Opps looks like there is no agent that can handle this request. Please contact us using the chat bubble bottom right so we can get this fixed for you!"
              : backendErrorMessage.includes(
                "old threads are deprecated. Please start a new thread",
              ) || backendErrorMessage?.includes("Conversation with id")
                ? "We couldn't find this conversation. It may have been deleted or expired."
                : backendErrorMessage?.includes(
                  "Triage service did not return an assistant_id",
                ) ||
                  backendErrorMessage
                    ?.toLowerCase()
                    ?.includes("no agent set up for this yet")
                  ? "No agent set up for this yet. Lest's add one."
                  : backendErrorMessage?.includes(
                    "Triage selected an unknown assistant",
                  ) ? "This agent has been paused or removed" : backendErrorMessage?.includes('Another process is currently operating on this conversation') ? "Another process is currently operating on this conversation. Please wait a moment and try again." : backendErrorMessage,
        classNames:
          statusCode === 500 || backendErrorMessage?.includes('Another process is currently operating on this conversation')
            ? "!bg-[#FEF7E1] border !border-[#FEF7E1]"
            : !backendErrorMessage.includes(
              "old threads are deprecated. Please start a new thread",
            ) && !backendErrorMessage?.includes("Conversation with id")
              ? "!bg-[#F0F1FF] border !border-[#C4D0FF]"
              : "",
        iconColor:
          statusCode === 500 || backendErrorMessage?.includes('Another process is currently operating on this conversation')
            ? "#ECB24B"
            : !backendErrorMessage.includes(
              "old threads are deprecated. Please start a new thread",
            ) && !backendErrorMessage?.includes("Conversation with id")
              ? "#5768FF"
              : "",
      });
      setIsUserChating(false);
      return;

    } finally {
      clearChatPending(payload?.conversation_id);
    }
  };

  useEffect(() => {
    if (!selectedAssistant?.assistant_id) return;
    createConvesation();
  }, [selectedAssistant]);

  const fetchSearchContent = async (search = "", page = 1, limit = 30) => {
    const response = await apiClient.get(
      `/actions/search?userId=${userInfo?._id}&locationId=${userActiveLocation?.id}&search=${search}&page=${page}&limit=${limit}`,
    );
    if (response?.data?.success) {
      page === 1
        ? setSearchPageContent({
          ...response.data.data,
          hasMore:
            response?.data?.data?.page * response?.data?.data?.limit <
            response?.data?.data?.total,
        })
        : setSearchPageContent((prev) => ({
          ...prev,
          data: [...prev.data, ...response.data?.data?.data],
          hasMore:
            response?.data?.data?.page * response?.data?.data?.limit <
            response?.data?.data?.total,
        }));
    } else {
      toast.error(response.data?.message);
    }
  };

  // locationId/agencyId overrides, same reason as fetchChats above — a
  // just-switched caller's closure still has the pre-switch location.
  const fetchTasksData = async (
    locationId = userActiveLocation?.id,
    agencyId = userActiveLocation?.agency_id,
  ) => {
    try {
      // type=mine: this agency/location's shared prompts aren't enough on
      // their own here — this feeds "Your Tasks" in ConversationWrapper.jsx,
      // which should show only prompts this user created or was shared on
      // (curated prompts still come through regardless). Other callers of
      // this same endpoint (frontend/simple-chat/groa-iframe/home-iframe)
      // omit type and keep the old unrestricted default.
      const response = await apiClient.get(
        `/prompts/prompt_data?userId=${userInfo?._id}&location_id=${locationId}&agency_id=${agencyId}&type=mine`,
      );
      if (response?.data?.success) setTasksData(response?.data?.data);
      else toast.error(response?.data?.message);
    } catch (err) {
      toast.error(err?.response?.data?.message);
    }
  };

  const removeTasksFav = async (e, id) => {
    const response = await apiClient.get(
      `/chat/remove/favourite?chatId=${id}&user_id=${userInfo?._id}&location_id=${userActiveLocation?.id}`,
    );
    if (response?.data?.success) {
      fetchTasksData();
    } else toast.error(response?.data?.message);
  };

  const addTasksFavourite = async (e, id) => {
    const response = await apiClient.get(
      `/chat/add/favourite?prompt_id=${id}&user_id=${userInfo?._id}&location_id=${userActiveLocation?.id}`,
    );
    if (response?.data?.success) {
      fetchTasksData();
    } else toast.error(response?.data?.message);
  };

  return (
    <UsersContext.Provider
      value={{
        setUseAgent,
        useAgent,
        contextUploadFileOnboarding,
        contextuploadFilefrominput,
        resetAttachedFileSize,
        getAttachedFileSize,
        users,
        SSO,
        setUsers,
        setUserAsUnread,
        userTyping,
        hideLeftSidebar,
        showLeftSidebar,
        isActiveSide,
        toast: toastState,
        showToast,
        hideToast,
        showExploreContent,
        enableExplore,
        enableChat,
        fromPromptMsg,
        getLocationUsers,
        locationUsers,
        isLocationUsersLoading,
        requestLocationUsers,
        companyInfo,
        checkfreetrial,
        selectedAssistant,
        setSelectedAssistant,
        products,
        setCompanyInfo,
        agentPageLoading,
        agents,
        fetchAgentsArray,
        agentBuilder,
        setOpenAgentBuilder,
        openAgentBuilder,
        conversationId,
        setConversationId,
        isAuto,
        setIsAuto,
        agency,
        isAgencyLoading,
        isAgencySuccess,
        agencyError,
        isHighestPlan,
        fetchChats,
        conversations,
        fetchMessages,
        skipNextFetchIdRef,
        markMessagePending,
        resumePendingChat,
        messages,
        setMessages,
        agentPlaceholderText,
        isUserChating,
        handleSendMessage,
        createConvesation,
        openShareModal,
        setOpenShareModal,
        chatError,
        setChatError,
        fetchSearchContent,
        searchPageContent,
        fetchTasksData,
        tasksData,
        addTasksFavourite,
        removeTasksFav,
        setIsUserChating
      }}
    >
      {children}
      <ContactToSupportModal
        isOpen={openLimitModal}
        onClose={() => setOpenLimitModal(false)}
      />
    </UsersContext.Provider>
  );
};

export { useUsersContext, UsersProvider };
